import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import SEO from '../components/SEO';
import EditorialSubNav from '../components/EditorialSubNav';
import articles from '../data/journalArticles';
import { localizeArticle } from '../data/journalArticles.i18n';
import { ArticleCard } from './Journal';
import { useT, useLang } from '../i18n/LanguageContext';

const eyebrowStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '9pt',
  fontWeight: 600,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: '#D6A87A',
};

const splitTitle = (article) => {
  const t = article.title.trimEnd();
  const accent = article.titleAccent;
  if (accent && t.endsWith(accent)) {
    const head = t.slice(0, t.length - accent.length).trimEnd();
    return { head, accent };
  }
  return { head: t, accent: '' };
};

/* ── Body block renderer ── */
const BlockRenderer = ({ block, isFirstParagraph }) => {
  if (block.type === 'paragraph') {
    if (isFirstParagraph) {
      // Drop cap on the first paragraph
      const text = block.text.trimStart();
      const firstChar = text.charAt(0);
      const rest = text.slice(1);
      return (
        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '15pt',
            lineHeight: 1.7,
            color: '#475067',
            marginBottom: '24px',
          }}
        >
          <span
            aria-hidden="false"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: '96pt',
              lineHeight: 0.9,
              color: '#D6A87A',
              float: 'left',
              paddingRight: '16px',
              paddingTop: '6px',
              fontWeight: 500,
            }}
          >
            {firstChar}
          </span>
          {rest}
        </p>
      );
    }
    return (
      <p
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '15pt',
          lineHeight: 1.7,
          color: '#475067',
          marginBottom: '24px',
        }}
      >
        {block.text}
      </p>
    );
  }

  if (block.type === 'subhead') {
    const t = block.text.trimEnd();
    const accent = block.accent;
    let head = t;
    let acc = '';
    if (accent && t.endsWith(accent)) {
      head = t.slice(0, t.length - accent.length).trimEnd();
      acc = accent;
    }
    return (
      <h2
        style={{
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: '26pt',
          lineHeight: 1.2,
          fontWeight: 500,
          color: '#0A132F',
          letterSpacing: '-0.005em',
          marginTop: '60px',
          marginBottom: '24px',
        }}
      >
        {head}{' '}
        {acc && (
          <em
            className="italic"
            style={{ color: '#B98556', fontWeight: 400 }}
          >
            {acc}
          </em>
        )}
      </h2>
    );
  }

  if (block.type === 'pullquote') {
    return (
      <figure
        className="my-12"
        style={{
          maxWidth: '70ch',
          marginLeft: 'auto',
          marginRight: 'auto',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            height: '1px',
            width: '32px',
            backgroundColor: 'rgba(214,168,122,0.55)',
            margin: '0 auto 32px',
          }}
        />
        <blockquote
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontStyle: 'italic',
            fontSize: '30pt',
            lineHeight: 1.3,
            fontWeight: 400,
            color: '#D6A87A',
            letterSpacing: '-0.005em',
          }}
        >
          {block.text}
        </blockquote>
        <div
          style={{
            height: '1px',
            width: '32px',
            backgroundColor: 'rgba(214,168,122,0.55)',
            margin: '32px auto 0',
          }}
        />
      </figure>
    );
  }

  if (block.type === 'image') {
    return (
      <figure
        className="my-10"
        style={{ width: '90%', margin: '40px auto' }}
      >
        <img src={block.src} alt={block.caption || ''} className="w-full h-auto" />
        {block.caption && (
          <figcaption
            className="mt-3 text-center"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              fontSize: '11pt',
              color: '#4A4F63',
              lineHeight: 1.55,
            }}
          >
            {block.caption}
          </figcaption>
        )}
      </figure>
    );
  }

  if (block.type === 'fullbleed') {
    return (
      <figure
        className="relative left-1/2 right-1/2 -mx-[50vw] w-screen my-12"
        style={{
          marginLeft: '-50vw',
          marginRight: '-50vw',
          left: '50%',
          right: '50%',
          position: 'relative',
        }}
      >
        <div
          style={{
            height: '1px',
            backgroundColor: 'rgba(214,168,122,0.45)',
            margin: '0 0 32px 0',
          }}
        />
        <img src={block.src} alt={block.caption || ''} className="w-full h-auto" />
        <div
          style={{
            height: '1px',
            backgroundColor: 'rgba(214,168,122,0.45)',
            margin: '32px 0 0 0',
          }}
        />
      </figure>
    );
  }

  return null;
};

const JournalArticle = () => {
  const t = useT();
  const { lang } = useLang();
  const { slug } = useParams();
  const baseArticle = articles.find((a) => a.slug === slug);

  if (!baseArticle) return <Navigate to="/journal" replace />;

  const article = localizeArticle(baseArticle, lang);

  const subNavLinks = [
    { label: t('subnav.headline'), target: 'headline' },
    { label: t('subnav.body'), target: 'body' },
    { label: t('subnav.author'), target: 'author' },
    { label: t('subnav.more'), target: 'more' },
  ];

  const titleParts = splitTitle(article);

  // "More from the Journal", same category first, fallback to most recent
  const sameCategory = articles.filter(
    (a) => a.slug !== baseArticle.slug && a.category === baseArticle.category
  );
  const others = articles.filter(
    (a) => a.slug !== baseArticle.slug && a.category !== baseArticle.category
  );
  const more = [...sameCategory, ...others].slice(0, 3);

  let firstParagraphRendered = false;

  return (
    <div data-testid={`journal-article-${slug}`}>
      <SEO
        title={`${article.title.replace(/\.$/, '')}, Journal | GlowMeets`}
        description={article.dek}
        keywords={`${article.category}, GlowMeets journal, ${article.title}`}
        url={`/journal/${article.slug}`}
        type="article"
        publishedTime={article.publishedISO}
      />

      {/* ───────────────────────── 1. ARTICLE HERO ───────────────────────── */}
      <section
        id="headline"
        className="relative w-full overflow-hidden bg-navy"
        style={{ minHeight: '100vh' }}
        data-testid="article-hero"
      >
        <img
          src={article.cover}
          alt={article.title}
          onError={(e) => {
            if (article.fallback && e.currentTarget.src !== article.fallback) {
              e.currentTarget.src = article.fallback;
            }
          }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Horizontal duotone fade */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to right, rgba(10,19,47,0.92) 0%, rgba(10,19,47,0.78) 38%, rgba(10,19,47,0.55) 65%, rgba(10,19,47,0.25) 100%)',
          }}
        />
        {/* Bottom soft fade */}
        <div
          className="absolute inset-x-0 bottom-0"
          style={{
            height: '180px',
            background:
              'linear-gradient(to bottom, rgba(10,19,47,0) 0%, #0A132F 100%)',
          }}
        />

        <div
          className="relative z-10 max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center"
          style={{
            minHeight: '100vh',
            paddingTop: '120px',
            paddingBottom: '90px',
          }}
        >
          <div className="max-w-[60ch] text-center">
            <div style={eyebrowStyle} className="mb-7">
              {article.category} · {article.publishedLabel} · {article.readMin} {t('common.minRead')}
            </div>

            <h1
              className="text-bone"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(36px, 4.6vw, 64pt)',
                lineHeight: 1.05,
                fontWeight: 500,
                letterSpacing: '-0.005em',
              }}
            >
              {titleParts.head}{' '}
              <em
                className="italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {titleParts.accent}
              </em>
            </h1>

            <p
              className="mt-8 mx-auto"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: '18pt',
                color: 'rgba(244,239,227,0.82)',
                lineHeight: 1.5,
                maxWidth: '60ch',
              }}
            >
              {article.dek}
            </p>

            <div
              className="mt-10 mx-auto"
              style={{
                height: '1px',
                width: '88px',
                backgroundColor: 'rgba(214,168,122,0.55)',
              }}
            />

            <p
              className="mt-7"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11pt',
                fontWeight: 600,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: '#D6A87A',
              }}
            >
              {t('common.by')} Ahmet Kazancı · GlowMeets · {article.publishedLabel}
            </p>
          </div>
        </div>
      </section>

      {/* Sticky in-page sub-nav */}
      <EditorialSubNav links={subNavLinks} surface="bone" />

      {/* ───────────────────────── 2. THE BODY ───────────────────────── */}
      <section
        id="body"
        className="bg-bone"
        style={{ paddingTop: '120px', paddingBottom: '120px' }}
        data-testid="article-body"
      >
        <article className="max-w-[660px] mx-auto px-4 sm:px-6 lg:px-8">
          {article.body.map((block, i) => {
            const isFirstP =
              !firstParagraphRendered && block.type === 'paragraph';
            if (isFirstP) firstParagraphRendered = true;
            return (
              <BlockRenderer
                key={`${slug}-block-${i}`}
                block={block}
                isFirstParagraph={isFirstP}
              />
            );
          })}

          {/* End-of-article footer */}
          <div
            className="mt-16 pt-10"
            style={{
              borderTop: '1px solid rgba(214,168,122,0.55)',
            }}
          >
            <p
              className="text-center mx-auto"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '10pt',
                fontWeight: 400,
                color: 'rgba(74,79,99,0.8)',
                lineHeight: 1.7,
                maxWidth: '60ch',
              }}
            >
              {t('journal.publishedNote', { date: article.publishedLabel.replace('MAY', 'May').replace('APRIL', 'April').replace('MARCH', 'March').replace('FEBRUARY', 'February').replace('JANUARY', 'January') })}
            </p>
            <div
              className="mt-6 mx-auto"
              style={{
                height: '1px',
                width: '60px',
                backgroundColor: 'rgba(214,168,122,0.6)',
              }}
            />
          </div>
        </article>
      </section>

      {/* ───────────────────────── 3. AUTHOR BLOCK ───────────────────────── */}
      <section
        id="author"
        className="bg-bone"
        style={{ paddingTop: '0', paddingBottom: '120px' }}
        data-testid="article-author"
      >
        <div className="max-w-[660px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-8 items-start">
            {/* LEFT, founder portrait (35%) */}
            <div className="sm:col-span-4">
              <div
                className="relative w-full overflow-hidden"
                style={{
                  aspectRatio: '3/4',
                  backgroundColor: '#0A132F',
                  border: '1px solid rgba(214,168,122,0.35)',
                }}
                data-testid="journal-author-portrait"
              >
                <img
                  src="/founder-640.jpg"
                  srcSet="/founder-320.jpg 320w, /founder-640.jpg 640w"
                  sizes="(max-width: 640px) 240px, 320px"
                  alt="Ahmet Kazancı, founder of GlowMeets"
                  className="absolute inset-0 w-full h-full"
                  style={{ objectFit: 'cover', objectPosition: 'center 22%' }}
                />
              </div>
            </div>

            {/* RIGHT, bio (65%) */}
            <div className="sm:col-span-8">
              <div style={eyebrowStyle} className="mb-3">
                {t('journal.aboutAuthor')}
              </div>
              <h3
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: '24pt',
                  fontWeight: 500,
                  color: '#0A132F',
                  letterSpacing: '-0.005em',
                }}
              >
                Ahmet Kazancı
              </h3>
              <p
                className="mt-3"
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontStyle: 'italic',
                  fontWeight: 400,
                  fontSize: '14pt',
                  color: '#4A4F63',
                  lineHeight: 1.6,
                }}
              >
                {t('journal.authorBio')}
              </p>

              <a
                href="https://www.linkedin.com/in/ahmetkazanci/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Ahmet Kazancı on LinkedIn"
                className="inline-flex items-center mt-5 hover:opacity-70 transition-opacity"
                data-testid="article-author-linkedin"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="#D6A87A" strokeWidth="1.5" />
                  <path
                    d="M8 10v7M8 7v.01M12 17v-4a2 2 0 0 1 4 0v4"
                    stroke="#D6A87A"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────────── 4. MORE FROM THE JOURNAL ───────────────────────── */}
      <section
        id="more"
        className="bg-bone"
        style={{ paddingTop: '100px', paddingBottom: '100px' }}
        data-testid="article-more"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div style={eyebrowStyle} className="mb-5">
              {t('journal.moreFromJournal.eyebrow')}
            </div>
            <h2
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(26px, 2.8vw, 32pt)',
                lineHeight: 1.15,
                fontWeight: 500,
                color: '#0A132F',
                letterSpacing: '-0.005em',
              }}
            >
              {t('journal.moreFromJournal.headline1')}{' '}
              <em
                className="italic"
                style={{ color: '#B98556', fontWeight: 400 }}
              >
                {t('journal.moreFromJournal.headline2')}
              </em>
            </h2>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
            style={{ gap: '48px 32px' }}
          >
            {more.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────── 5. CLOSING BAND ───────────────────────── */}
      <section
        className="bg-navy text-bone w-full text-center"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="article-closing"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center">
          <img
            src="/glowmeets-mark.png"
            alt="GlowMeets compass mark"
            style={{ height: '56px', width: 'auto' }}
          />

          <p
            className="mt-7"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: '14pt',
              color: 'rgba(244,239,227,0.7)',
              lineHeight: 1.5,
            }}
          >
            {t('journal.closingArticle')}
          </p>

          <Link
            to="/contact"
            data-testid="article-cta"
            className="inline-flex items-center justify-center mt-8 transition-colors"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '13pt',
              fontWeight: 600,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#D6A87A',
              backgroundColor: 'transparent',
              border: '1px solid #D6A87A',
              padding: '0 28px',
              borderRadius: '999px',
              minWidth: '280px',
              height: '56px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#D6A87A';
              e.currentTarget.style.color = '#0A132F';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#D6A87A';
            }}
          >
            {t('cta.bookConsultation')}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default JournalArticle;
